import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { SystemNotificationApiModel } from '../../../api/systemNotifications/models/system-notification.api.model';
import { SystemNotificationsStatusEnum } from '../enums/system-notifications-status.enum';
import { SystemNotificationsStyleStateEnum } from '../enums/system-notifications-style-state.enum';
import { SystemNotificationsTypeEnum } from '../enums/system-notifications-type.enum';
import moment from 'moment-timezone';

interface SystemNotificationDateRangeModel {
    start: Date;
    end: Date;
    timeZone: string;

    // for FE use only, we need to know the time offset by the timezone, because javasript Date always converts it to locale time
    startConvertedwithTz: Date;
    endConvertedwithTz: Date;
}
interface StatusBadge {
    status: SystemNotificationsStatusEnum;
    iconClass?: string;
    tooltip?: string;
}
export class SystemNotificationModel implements AuditableModel {
    static readonly SESSION_STORAGE_KEY_BASE = 'system_notification_';

    id: number;
    title: string;
    message: string;
    type: SystemNotificationsTypeEnum;
    styleState?: SystemNotificationsStyleStateEnum;
    notificationTimeRange: SystemNotificationDateRangeModel;
    maintenanceTimeRange?: SystemNotificationDateRangeModel;
    createdByUserId?: number;
    createdDate?: Date;
    modifiedByUserId?: number;
    modifiedDate?: Date;
    statusBadge?: StatusBadge;

    constructor(apiModel?: SystemNotificationApiModel) {
        if (apiModel) {
            this.id = apiModel.id;
            this.title = apiModel.title;
            this.message = apiModel.message;
            this.notificationTimeRange = {
                start: SystemNotificationModel.dateInTimeZone(apiModel.notificationStartDate, apiModel.notificationTimeZone),
                end: SystemNotificationModel.dateInTimeZone(apiModel.notificationEndDate, apiModel.notificationTimeZone),
                timeZone: apiModel.notificationTimeZone,
                startConvertedwithTz: new Date(apiModel.notificationStartDate),
                endConvertedwithTz: new Date(apiModel.notificationEndDate)
            };
            this.type = apiModel.type;

            if (apiModel.maintenanceEndDate && apiModel.maintenanceStartDate) {
                this.maintenanceTimeRange = {
                    start: SystemNotificationModel.dateInTimeZone(apiModel.maintenanceStartDate, apiModel.maintenanceTimeZone),
                    end: SystemNotificationModel.dateInTimeZone(apiModel.maintenanceEndDate, apiModel.maintenanceTimeZone),
                    timeZone: apiModel.maintenanceTimeZone || '',
                    startConvertedwithTz: new Date(apiModel.maintenanceStartDate),
                    endConvertedwithTz: new Date(apiModel.maintenanceEndDate)
                };
            }
            this.styleState = apiModel.style;
            this.createdByUserId = apiModel.createdByUserId;
            this.createdDate = apiModel.createdDate ? new Date(apiModel.createdDate) : null;
            this.modifiedByUserId = apiModel.lastModifiedByUserId;
            this.modifiedDate = apiModel.lastModifiedDate ? new Date(apiModel.lastModifiedDate) : null;

            this.setStatusBadge(apiModel.notificationStartDate, apiModel.notificationEndDate);
        }
    }

    static dateInTimeZone(timestamp: number, timeZone?: string): Date {
        const date = moment(timestamp).tz(timeZone || 'UTC');
        return new Date(
            date.year(),
            date.month(),
            date.date(),
            date.hour(),
            date.minute(),
            date.second(),
            date.millisecond()
        );
    }

    static toUnixTimestamp(date: Date, timeZone: string): number {
        return moment.tz([
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds()
        ], timeZone || 'UTC').valueOf();
    }

    toApiModel(): SystemNotificationApiModel {
        return {
            id: this.id,
            title: this.title,
            message: this.message,
            type: this.type,
            style: this.styleState,
            notificationStartDate: SystemNotificationModel.toUnixTimestamp(this.notificationTimeRange.start, this.notificationTimeRange.timeZone),
            notificationEndDate: SystemNotificationModel.toUnixTimestamp(this.notificationTimeRange.end, this.notificationTimeRange.timeZone),
            maintenanceStartDate: this.maintenanceTimeRange
                ? SystemNotificationModel.toUnixTimestamp(this.maintenanceTimeRange.start, this.maintenanceTimeRange.timeZone)
                : null,
            maintenanceEndDate: this.maintenanceTimeRange
                ? SystemNotificationModel.toUnixTimestamp(this.maintenanceTimeRange.end, this.maintenanceTimeRange.timeZone)
                : null,
            createdByUserId: this.createdByUserId,
            createdDate: +this.createdDate,
            lastModifiedByUserId: this.modifiedByUserId,
            lastModifiedDate: +this.modifiedDate,
            notificationTimeZone: this.notificationTimeRange.timeZone ? this.notificationTimeRange.timeZone : null,
            maintenanceTimeZone: this.maintenanceTimeRange?.timeZone ? this.maintenanceTimeRange.timeZone : null
        };
    }

    clone(): SystemNotificationModel {
        return new SystemNotificationModel(this.toApiModel());
    }

    setStatusBadge(start: number, end: number) {
        const now = new Date().getTime();
        if (now >= start && now <= end) {
            this.statusBadge = {
                status: SystemNotificationsStatusEnum.active,
                iconClass: 'agpr-i-badge-active'
            };
        }
        if (now < start) {
            this.statusBadge = {
                status: SystemNotificationsStatusEnum.pending,
                iconClass: 'agpr-i-badge-pending'
            };
        }
        if (now > end) {
            this.statusBadge = {
                status: SystemNotificationsStatusEnum.past,
                iconClass: 'agpr-i-badge-history'
            };
        }
        this.statusBadge.tooltip = `SystemNotification.status.${this.statusBadge.status}`;
    }

    getSessionStorageKey(): string {
        return SystemNotificationModel.SESSION_STORAGE_KEY_BASE + this.id;
    }


    setLocalDateToUtc(date: Date = new Date()): Date {
        const offsetInMilliseconds = date.getTimezoneOffset() * 60 * 1000;
        return new Date(date.getTime() - offsetInMilliseconds);
    }

    getUtcToLocalDate(date: Date = new Date()): Date {
        const offsetInMilliseconds = date.getTimezoneOffset() * 60 * 1000;
        return new Date(date.getTime() + offsetInMilliseconds);
    }

    convertDateToUnixTimestamp(inputDate: Date, timeZone: string, inSeconds = false): number {
        const timestamp = SystemNotificationModel.toUnixTimestamp(inputDate, timeZone);
        return inSeconds ? Math.floor(timestamp / 1000) : timestamp;
    }

}
